import type {ConnectionOperation, EntityOperation} from '@github-ui/optimistic-connection'
import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useQueryClient} from '@github-ui/react-query'
import type {AddBlockedByMutationMutation} from '@github-ui/server-persisted-queries/types/addBlockedByMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {ConnectionHandler, useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import {
  BLOCKED_BY_CONNECTION_PATH,
  BLOCKING_CONNECTION_PATH,
} from '../components/sections/relations-section/dependency-issue-query-types'
import {dependenciesPickerRelationshipsQueryKey} from '../components/sections/relations-section/fetch-dependencies-picker-relationships'
import {BLOCKED_BY_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsBlockedByListView'
import {BLOCKING_LIST_ALL_RELAY_CONNECTION} from '../components/sections/relations-section/LazyRelationshipsBlockingListView'
import {escapeOptimisticTitleHTML} from '../utils/helpers'

export type OptimisticDependencyIssue = {
  id: string
  title: string
  url: string
  number: number
  state: string
  stateReason?: string | null
  repository: {id: string; nameWithOwner: string}
}

type DependencyDirection = 'blockedBy' | 'blocking'
type AddBlockedByVariables = {issueId: string; nameWithOwner: string; issueNumber: number}
type AddBlockedByOperation = Extract<ConnectionOperation, {type: 'addToConnection'}>
type CountPublication = {
  completed: (operation: EntityOperation) => void
  failed: (error: Error) => void
}
type MutationContext = {relayUpdate: Disposable; countPublication?: CountPublication}
type MutationOptions = Omit<
  Parameters<typeof useConnectionMutation<AddBlockedByMutationMutation, Error, MutationContext>>[0],
  'ownerId' | 'connectionPath'
>

function connectionPath(direction: DependencyDirection) {
  return direction === 'blockedBy' ? BLOCKED_BY_CONNECTION_PATH : BLOCKING_CONNECTION_PATH
}

function directionFor(operation: AddBlockedByOperation): DependencyDirection {
  return operation.connectionPath[0] === 'topBlocking' ? 'blocking' : 'blockedBy'
}

function toOptimisticOperation(
  dependencyIssue: OptimisticDependencyIssue,
  direction: DependencyDirection,
): AddBlockedByOperation {
  const [owner, name] = dependencyIssue.repository.nameWithOwner.split('/')
  return {
    type: 'addToConnection',
    connectionPath: connectionPath(direction),
    anchor: {at: 'end'},
    counts: [{path: ['issueDependenciesSummary', direction], delta: 1}],
    node: {
      ...dependencyIssue,
      titleHTML: escapeOptimisticTitleHTML(dependencyIssue.title),
      repository: {...dependencyIssue.repository, name, owner: {login: owner}},
    },
  }
}

async function addBlockedByRequest({
  nameWithOwner,
  issueNumber,
  dependencyIssueId,
  direction,
}: AddBlockedByVariables & {dependencyIssueId: string; direction: DependencyDirection}) {
  const relationship = direction === 'blockedBy' ? 'blocked_by' : 'blocking'
  const body = direction === 'blockedBy' ? {blockingIssueId: dependencyIssueId} : {issueId: dependencyIssueId}
  const response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/relationships/${relationship}.json`, {
    method: 'POST',
    body,
  })
  const result = (await response.json()) as {
    data?: AddBlockedByMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  const error = result.errors?.find(item => item.message)?.message
  if (!response.ok || error || !result.data?.addBlockedBy?.issue || !result.data.addBlockedBy.blockingIssue) {
    throw new Error(error ?? 'Failed to add dependency')
  }
  return result.data
}

export type DependencyMutationSide = NonNullable<NonNullable<AddBlockedByMutationMutation['addBlockedBy']>['issue']>

export function writeConfirmedDependencySide(store: RecordSourceProxy, side: DependencyMutationSide): void {
  const issueRecord = store.get(side.id)
  if (!issueRecord) return

  for (const [field, connection] of [
    ['blockedBy', side.topBlockedBy],
    ['blocking', side.topBlocking],
  ] as const) {
    const connectionRecord = issueRecord.getLinkedRecord(field, {first: 3, ranked: true})
    if (!connectionRecord) continue
    const nodes = (connection.nodes ?? []).flatMap(node => {
      if (!node) return []
      let nodeRecord = store.get(node.id)
      if (!nodeRecord) nodeRecord = store.create(node.id, 'Issue')
      nodeRecord.setValue(node.title, 'title')
      // eslint-disable-next-line @github-ui/github-monorepo/no-unsafe-titlehtml
      nodeRecord.setValue(node.titleHTML, 'titleHTML')
      nodeRecord.setValue(node.url, 'url')
      nodeRecord.setValue(node.number, 'number')
      nodeRecord.setValue(node.state, 'state')
      nodeRecord.setValue(node.stateReason, 'stateReason')

      let repositoryRecord = store.get(node.repository.id)
      if (!repositoryRecord) repositoryRecord = store.create(node.repository.id, 'Repository')
      repositoryRecord.setValue(node.repository.name, 'name')
      repositoryRecord.setValue(node.repository.nameWithOwner, 'nameWithOwner')
      let ownerRecord = store.get(node.repository.owner.id)
      if (!ownerRecord) ownerRecord = store.create(node.repository.owner.id, node.repository.owner.__typename)
      ownerRecord.setValue(node.repository.owner.login, 'login')
      repositoryRecord.setLinkedRecord(ownerRecord, 'owner')
      nodeRecord.setLinkedRecord(repositoryRecord, 'repository')
      return [nodeRecord]
    })
    connectionRecord.setLinkedRecords(nodes, 'nodes')
    connectionRecord.getLinkedRecord('pageInfo')?.setValue(connection.pageInfo.hasNextPage, 'hasNextPage')
  }

  const summary = issueRecord.getLinkedRecord('issueDependenciesSummary')
  summary?.setValue(side.issueDependenciesSummary.blockedBy, 'blockedBy')
  summary?.setValue(side.issueDependenciesSummary.blocking, 'blocking')
}

export function invalidateRelayDependencyConnections(
  store: RecordSourceProxy,
  issueId: string,
  blockingIssueId: string,
): void {
  store.get(issueId)?.getLinkedRecord('blockedBy', {first: 100})?.invalidateRecord()
  store.get(blockingIssueId)?.getLinkedRecord('blocking', {first: 100})?.invalidateRecord()
  store.get(ConnectionHandler.getConnectionID(issueId, BLOCKED_BY_LIST_ALL_RELAY_CONNECTION))?.invalidateRecord()
  store.get(ConnectionHandler.getConnectionID(blockingIssueId, BLOCKING_LIST_ALL_RELAY_CONNECTION))?.invalidateRecord()
}

export function useAddBlockedByMutation({issueId, nameWithOwner, issueNumber}: AddBlockedByVariables) {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const [owner, repo] = nameWithOwner.split('/')
  const secondaryQueryKey = issueViewerSecondaryViewQueryKey(owner ?? '', repo ?? '', issueNumber)
  const pickerQueryKey = dependenciesPickerRelationshipsQueryKey(nameWithOwner, issueNumber)

  const mutationOptions = {
    mutationFn: operation => {
      const addOperation = operation as AddBlockedByOperation
      return addBlockedByRequest({
        nameWithOwner,
        issueNumber,
        issueId,
        dependencyIssueId: addOperation.node.id,
        direction: directionFor(addOperation),
      })
    },
    onMutate: ({operation}) => {
      const addOperation = operation as AddBlockedByOperation
      const direction = directionFor(addOperation)
      const dependencyIssue = addOperation.node as unknown as OptimisticDependencyIssue
      const countOperation: EntityOperation | undefined =
        direction === 'blockedBy'
          ? {type: 'deltaEntityField', id: issueId, field: 'blockedBy', delta: 1, clamp: {min: 0}}
          : undefined
      return {
        relayUpdate: environment.applyUpdate({
          storeUpdater: store => optimisticAddBlockedBy(store, issueId, dependencyIssue, direction),
        }),
        countPublication: countOperation ? publishOptimisticFields(countOperation) : undefined,
      }
    },
    onSuccess: async (data, variables, context) => {
      const payload = data.addBlockedBy
      const addOperation = variables.operation as AddBlockedByOperation
      const direction = directionFor(addOperation)
      const currentIssue = direction === 'blockedBy' ? payload?.issue : payload?.blockingIssue
      try {
        if (currentIssue) {
          await settleConfirmedOperation({
            queryClient,
            targets: [{queryKey: secondaryQueryKey}],
            ownerId: issueId,
            operation: {
              type: 'setFields',
              id: issueId,
              fields: {
                topBlockedBy: currentIssue.topBlockedBy,
                topBlocking: currentIssue.topBlocking,
                issueDependenciesSummary: currentIssue.issueDependenciesSummary,
              },
            },
          })
        }
        await queryClient.invalidateQueries({queryKey: pickerQueryKey})
        commitLocalUpdate(environment, store => {
          if (payload?.issue) writeConfirmedDependencySide(store, payload.issue)
          if (payload?.blockingIssue) writeConfirmedDependencySide(store, payload.blockingIssue)
          invalidateRelayDependencyConnections(
            store,
            payload?.issue?.id ?? (direction === 'blockedBy' ? issueId : addOperation.node.id),
            payload?.blockingIssue?.id ?? (direction === 'blocking' ? issueId : addOperation.node.id),
          )
        })
        const blockedByCount = payload?.issue?.issueDependenciesSummary.blockedBy
        if (context?.countPublication && typeof blockedByCount === 'number' && payload?.issue) {
          context.countPublication.completed({
            type: 'setFields',
            id: payload.issue.id,
            fields: {blockedBy: blockedByCount},
          })
        }
      } finally {
        context?.relayUpdate.dispose()
      }
    },
    onError: (error, _variables, context) => {
      context?.countPublication?.failed(error)
      context?.relayUpdate.dispose()
    },
  } satisfies MutationOptions

  const {mutateAsync: mutateBlockedBy} = useConnectionMutation<AddBlockedByMutationMutation, Error, MutationContext>({
    ...mutationOptions,
    ownerId: issueId,
    connectionPath: BLOCKED_BY_CONNECTION_PATH,
  })
  const {mutateAsync: mutateBlocking} = useConnectionMutation<AddBlockedByMutationMutation, Error, MutationContext>({
    ...mutationOptions,
    ownerId: issueId,
    connectionPath: BLOCKING_CONNECTION_PATH,
  })

  return useCallback(
    (dependencyIssue: OptimisticDependencyIssue, direction: DependencyDirection) =>
      (direction === 'blockedBy' ? mutateBlockedBy : mutateBlocking)({
        operation: toOptimisticOperation(dependencyIssue, direction),
      }),
    [mutateBlockedBy, mutateBlocking],
  )
}

export function optimisticAddBlockedBy(
  store: RecordSourceProxy,
  issueId: string,
  optimisticIssue: OptimisticDependencyIssue,
  direction: DependencyDirection,
) {
  const issueRecord = store.get(issueId)
  if (!issueRecord) return
  let dependencyRecord = store.get(optimisticIssue.id)
  if (!dependencyRecord) dependencyRecord = store.create(optimisticIssue.id, 'Issue')
  dependencyRecord.setValue(optimisticIssue.title, 'title')
  dependencyRecord.setValue(escapeOptimisticTitleHTML(optimisticIssue.title), 'titleHTML')
  dependencyRecord.setValue(optimisticIssue.url, 'url')
  dependencyRecord.setValue(optimisticIssue.number, 'number')
  dependencyRecord.setValue(optimisticIssue.state, 'state')
  dependencyRecord.setValue(optimisticIssue.stateReason ?? null, 'stateReason')

  let repositoryRecord = store.get(optimisticIssue.repository.id)
  if (!repositoryRecord) repositoryRecord = store.create(optimisticIssue.repository.id, 'Repository')
  repositoryRecord.setValue(optimisticIssue.repository.nameWithOwner, 'nameWithOwner')
  const [owner, name] = optimisticIssue.repository.nameWithOwner.split('/')
  repositoryRecord.setValue(name, 'name')
  let ownerRecord = repositoryRecord.getLinkedRecord('owner')
  if (!ownerRecord) {
    ownerRecord = store.create(`${optimisticIssue.repository.id}:owner:optimistic`, 'Organization')
    repositoryRecord.setLinkedRecord(ownerRecord, 'owner')
  }
  ownerRecord.setValue(owner, 'login')
  dependencyRecord.setLinkedRecord(repositoryRecord, 'repository')

  const connectionField = direction === 'blockedBy' ? 'blockedBy' : 'blocking'
  const connection = issueRecord.getLinkedRecord(connectionField, {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    if (!existingNodes.some(node => node?.getDataID() === optimisticIssue.id)) {
      connection.setLinkedRecords([...existingNodes, dependencyRecord], 'nodes')
    }
  }
  const summary = issueRecord.getLinkedRecord('issueDependenciesSummary')
  const currentCount = (summary?.getValue(connectionField) as number) || 0
  summary?.setValue(currentCount + 1, connectionField)
}
